import type { CSSProperties } from "react";

/**
 * Fixed brand accent for SUPER_TENANT rows (e.g. Adge). Used anywhere a numeric
 * accent feeds the `--org-accent` pipeline (borders, washes) so the platform org
 * reads as intentionally branded amber rather than a random per-name hash color.
 * Pair with the `iam-super-tenant-card__*` animated mesh/shimmer classes for the
 * premium treatment instead of the static per-tenant wash.
 */
export const SUPER_TENANT_ACCENT = "#f59e0b";

/** CSS variable hook for dynamic org/person accent colors in Tailwind arbitrary values. */
export function orgAccentVar(accent: string): CSSProperties {
  return { "--org-accent": accent } as CSSProperties;
}

/** Card/drawer body wash — subtle tint on light `--card`, visible tint on dark `--card`. */
export const ORG_ACCENT_WASH_CLASS =
  "bg-[linear-gradient(145deg,color-mix(in_srgb,var(--org-accent)_15%,var(--card))_0%,color-mix(in_srgb,var(--org-accent)_6%,var(--card))_40%,var(--card)_100%)] dark:bg-[linear-gradient(145deg,color-mix(in_srgb,var(--org-accent)_22%,var(--card))_0%,color-mix(in_srgb,var(--org-accent)_9%,var(--card))_42%,var(--card)_100%)]";

/** Header wash inside drawers. */
export const ORG_ACCENT_HEADER_WASH_CLASS =
  "bg-[linear-gradient(145deg,color-mix(in_srgb,var(--org-accent)_12%,var(--card))_0%,color-mix(in_srgb,var(--org-accent)_4%,var(--card))_50%,transparent_100%)] dark:bg-[linear-gradient(145deg,color-mix(in_srgb,var(--org-accent)_16%,var(--card))_0%,color-mix(in_srgb,var(--org-accent)_6%,var(--card))_52%,transparent_100%)]";

/** Compact list row wash (e.g. org rows in PersonDrawer). */
export const ORG_ACCENT_ROW_WASH_CLASS =
  "bg-[linear-gradient(145deg,color-mix(in_srgb,var(--org-accent)_18%,var(--card))_0%,color-mix(in_srgb,var(--org-accent)_8%,var(--card))_45%,var(--card)_100%)] dark:bg-[linear-gradient(145deg,color-mix(in_srgb,var(--org-accent)_20%,var(--card))_0%,color-mix(in_srgb,var(--org-accent)_8%,var(--card))_45%,var(--card)_100%)]";

/** Inline row surface — reliable accent border + CSS var for child utilities. */
export function orgAccentRowSurfaceStyle(accent: string): CSSProperties {
  return {
    ...orgAccentVar(accent),
    borderColor: `color-mix(in srgb, ${accent} 30%, var(--border))`,
  };
}

/** Inline row wash — uses `--card` so it adapts in light and dark. */
export function orgAccentRowWashStyle(accent: string): CSSProperties {
  return {
    background: `linear-gradient(145deg, color-mix(in srgb, ${accent} 18%, var(--card)) 0%, color-mix(in srgb, ${accent} 8%, var(--card)) 45%, var(--card) 100%)`,
  };
}

/** Inline row wash — dark theme. */
export function orgAccentRowWashStyleDark(accent: string): CSSProperties {
  return {
    background: `linear-gradient(145deg, color-mix(in srgb, ${accent} 22%, var(--card)) 0%, color-mix(in srgb, ${accent} 10%, var(--card)) 45%, var(--card) 100%)`,
  };
}

/**
 * Table-cell wash — fades left→right instead of diagonally. A `<td>` is much
 * wider than it is tall, so the 145deg row wash above reaches its `var(--card)`
 * stop at a different point along the top edge vs. the bottom edge, leaving a
 * faint diagonal seam right where the cell meets its neighbor. Resolving to
 * flat `var(--card)` well before 100% (`85%`) guarantees the whole right edge
 * matches the next cell exactly, regardless of row height.
 */
export function orgAccentTableCellWashStyle(accent: string): CSSProperties {
  return {
    background: `linear-gradient(to right, color-mix(in srgb, ${accent} 18%, var(--card)) 0%, color-mix(in srgb, ${accent} 8%, var(--card)) 35%, var(--card) 85%)`,
  };
}

/** Table-cell wash — dark theme. */
export function orgAccentTableCellWashStyleDark(accent: string): CSSProperties {
  return {
    background: `linear-gradient(to right, color-mix(in srgb, ${accent} 22%, var(--card)) 0%, color-mix(in srgb, ${accent} 10%, var(--card)) 35%, var(--card) 85%)`,
  };
}

/** Dark hover/selected boost — stronger mix for visibility on `bg-muted` popovers. */
export function orgAccentRowWashStyleDarkHover(accent: string): CSSProperties {
  return {
    background: `linear-gradient(145deg, color-mix(in srgb, ${accent} 34%, var(--card)) 0%, color-mix(in srgb, ${accent} 16%, var(--card)) 48%, color-mix(in srgb, ${accent} 8%, var(--card)) 100%)`,
  };
}

/** Card wash inline — light theme. */
export function orgAccentCardWashStyle(accent: string): CSSProperties {
  return {
    background: `linear-gradient(145deg, color-mix(in srgb, ${accent} 15%, var(--card)) 0%, color-mix(in srgb, ${accent} 6%, var(--card)) 40%, var(--card) 100%)`,
  };
}

/** Card wash inline — dark theme (stronger mix on `--card`). */
export function orgAccentCardWashStyleDark(accent: string): CSSProperties {
  return {
    background: `linear-gradient(145deg, color-mix(in srgb, ${accent} 22%, var(--card)) 0%, color-mix(in srgb, ${accent} 9%, var(--card)) 42%, var(--card) 100%)`,
  };
}

/** Top accent stripe on cards. */
export const ORG_ACCENT_TOP_STRIPE_CLASS = "opacity-75 dark:opacity-80 group-hover:opacity-90";

/** Drawer shell top edge — 2px top stripe that fades left→right to match the header wash. */
export const ORG_ACCENT_DRAWER_TOP_CLASS =
  "bg-[length:100%_2px] bg-no-repeat bg-[linear-gradient(to_right,color-mix(in_srgb,var(--org-accent)_72%,var(--border))_0%,transparent_100%)] dark:bg-[linear-gradient(to_right,var(--org-accent)_0%,transparent_100%)]";

/** Logo/photo frame border. */
export const ORG_ACCENT_FRAME_BORDER_CLASS =
  "border-[color-mix(in_srgb,var(--org-accent)_58%,var(--border))] dark:border-[color-mix(in_srgb,var(--org-accent)_48%,var(--border))]";

/** Idle card border tint. */
export const ORG_ACCENT_CARD_BORDER_CLASS =
  "border-[color-mix(in_srgb,var(--org-accent)_22%,var(--border))] dark:border-[color-mix(in_srgb,var(--org-accent)_30%,var(--border))]";

/** Dashed border for add/placeholder rows. */
export const ORG_ACCENT_ROW_DASHED_BORDER_CLASS =
  "border-[color-mix(in_srgb,var(--org-accent)_26%,var(--border))] dark:border-[color-mix(in_srgb,var(--org-accent)_32%,var(--border))]";

/** Selected card border. */
export const ORG_ACCENT_CARD_BORDER_ACTIVE_CLASS =
  "border-[color-mix(in_srgb,var(--org-accent)_34%,var(--border))]";
